import {
    canonicalTimeZone,
    isValidLocalDateTime,
    localToUtc,
} from '@content-scheduler/shared/render';

export type EventDate = { start: string; end: string; allDay: boolean; timeZone: string };

export type EventDateText = {
    /** For the calendar tile: "Nov" and "22". */
    month: string;
    day: string;
    /** The full line, e.g. "Sat, Nov 22 · 10:00 AM – 2:00 PM CST". */
    when: string;
    /** `when` split where a line break reads naturally ("Sat, Nov 22" | "10:00 AM – 2:00 PM CST"). */
    segments: string[];
    /** For `<time dateTime>`. */
    iso: string;
};

/** Midnight UTC of a wall date, for formatting calendar dates without zone shifts. */
const calendarDay = (local: string) => {
    const [y, m, d] = local.slice(0, 10).split('-').map(Number) as [number, number, number];
    return new Date(Date.UTC(y, m - 1, d));
};

/**
 * How an event's date reads to visitors. Times are shown in the event's own
 * time zone with its abbreviation, since visitors come to a physical place.
 */
export function formatEventDate(date: EventDate, locale?: string): EventDateText | null {
    if (!isValidLocalDateTime(date.start)) return null;
    const zone = canonicalTimeZone(date.timeZone) ?? 'UTC';
    const hasEnd = isValidLocalDateTime(date.end) && date.end > date.start;
    const sameDay = !hasEnd || date.end.slice(0, 10) === date.start.slice(0, 10);
    const startDay = calendarDay(date.start);

    const inUtc = (options: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' });
    const inZone = (options: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(locale, { ...options, timeZone: zone });

    const tile = {
        month: inUtc({ month: 'short' }).format(startDay),
        day: inUtc({ day: 'numeric' }).format(startDay),
    };

    if (date.allDay) {
        const when = sameDay
            ? inUtc({ weekday: 'long', month: 'long', day: 'numeric' }).format(startDay)
            : inUtc({ month: 'short', day: 'numeric' }).formatRange(
                  startDay,
                  calendarDay(date.end),
              );
        return { ...tile, when, segments: [when], iso: date.start.slice(0, 10) };
    }

    const start = localToUtc(date.start, zone);
    const end = hasEnd ? localToUtc(date.end, zone) : null;
    const zoneName =
        inZone({ timeZoneName: 'short' })
            .formatToParts(start)
            .find((part) => part.type === 'timeZoneName')?.value ?? zone;
    const day = inZone({ weekday: 'short', month: 'short', day: 'numeric' }).format(start);
    const time = inZone({ hour: 'numeric', minute: '2-digit' });

    const dayAndTime = inZone({
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
    const segments =
        end && !sameDay
            ? [`${dayAndTime.formatRange(start, end)} ${zoneName}`]
            : [day, `${end ? time.formatRange(start, end) : time.format(start)} ${zoneName}`];
    return { ...tile, when: segments.join(' · '), segments, iso: start.toISOString() };
}
